import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { createEditJobOptionsQuery } from "@/hooks/useConfigOptions";

const CUSTOM_ITEM_NAME = "Custom";

type ItemUnit = {
  unit: string;
  cost_per_unit: number;
  price_per_unit: number;
};

/**
 * Fetches quote item config (kits scoped to the given repair system, plus repair options) and exposes:
 * - `itemOptions`: the combined, deduplicated-by-name list for the Item combobox.
 * - `getUnitsForItem`: the available units (with their cost/price) for a given item name.
 *
 * @param repairSystemId - id of the repair system the quote belongs to (from the `repair_system` URL param).
 * When absent, falls back to kits from every repair system.
 */
export function useQuoteItemOptions(repairSystemId?: string | null) {
  const { data: config } = useQuery(createEditJobOptionsQuery);

  const kits = useMemo(() => {
    if (!repairSystemId) return config?.kits ?? [];
    const matchedSystem = config?.repair_systems.find((system) => String(system.id) === repairSystemId);
    return matchedSystem?.kits ?? [];
  }, [config?.kits, config?.repair_systems, repairSystemId]);

  const itemUnitsByName = useMemo(() => {
    const map = new Map<string, ItemUnit[]>();

    for (const source of [kits, config?.repair_options ?? []]) {
      for (const entry of source) {
        if (!entry.name) continue;
        const units = map.get(entry.name) ?? [];
        const isDuplicateUnit = units.some((existing) => existing.unit === entry.unit);
        if (entry.unit && !isDuplicateUnit) {
          units.push({
            unit: entry.unit,
            cost_per_unit: entry.cost_per_unit ?? 0,
            price_per_unit: entry.price_per_unit ?? 0,
          });
        }
        map.set(entry.name, units);
      }
    }

    return map;
  }, [kits, config?.repair_options]);

  const itemOptions = useMemo(() => {
    return [
      { label: CUSTOM_ITEM_NAME, value: CUSTOM_ITEM_NAME },
      ...[...itemUnitsByName.keys()].map((name) => ({ label: name, value: name })),
    ];
  }, [itemUnitsByName]);

  function getUnitsForItem(itemName: string): ItemUnit[] {
    return itemUnitsByName.get(itemName) ?? [];
  }

  return { itemOptions, getUnitsForItem, isCustomItem: (itemName: string) => itemName === CUSTOM_ITEM_NAME };
}
